import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UUIDSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import { DBTimestampSchema } from '../common/sql';
import {
  DBQuestionOccurrenceSchema,
  QuestionOccurrenceOriginalQuestionIdSchema,
  QuestionOccurrenceOriginalQuestionNumberSchema,
} from '../questionOccurrences/schemas';
import {
  DBQuestionSchema,
  DBQuestionStatusSchema,
  QuestionBodyLatexSchema,
  QuestionStatusSchema,
} from '../questions/schemas';
import { DBSourceDocumentYearSchema } from '../sourceDocuments/schemas';
import { TaxonomyNodeKind, TaxonomyStatus } from './enums';

// String bounds match the tables' CHECK constraints. A tighter bound would log a
// warning for rows the database accepts.

/** A key names the item, not its place in a tree, so moving the item keeps its key. */
export const DBTaxonomyKeySchema = z
  .string()
  .regex(/^[a-z0-9]+([-/][a-z0-9]+)*$/, 'A key is lowercase words joined by - or /')
  .nullable();

export const DBTopicNameSchema = AllCharLabelSchema.min(1);
export const DBTopicDescriptionSchema = AllCharLabelSchema.nullable();
export const DBTopicStatusSchema = z.enum(TaxonomyStatus);

export const DBTopicSchema = z.object({
  id: UUIDSchema,
  key: DBTaxonomyKeySchema,
  name: DBTopicNameSchema,
  description: DBTopicDescriptionSchema,
  kindId: UUIDSchema.nullable(),
  status: DBTopicStatusSchema,
  mergedIntoTopicId: UUIDSchema.nullable(),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBTopicAliasSchema = z.object({
  id: UUIDSchema,
  topicId: UUIDSchema,
  alias: AllCharLabelSchema.min(1),
  createdAt: DBTimestampSchema,
});

/**
 * A topic can belong to more than one subject, so subject-specific fields are
 * stored here, not on the topic.
 */
export const DBTopicSubjectSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  sourceTopicId: z.string().min(1).nullable(),
  mappingScopeId: UUIDSchema,
  primaryGeneralGroupId: UUIDSchema.nullable(),
  taggingNote: AllCharLabelSchema.nullable(),
  reviewSources: z.array(z.string()).nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBTopicSubjectSyllabusSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  groupingId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

/** Links a broad topic to a narrower one that taggers should usually choose over it. */
export const DBTopicRelatedSpecificSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  specificTopicId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

/**
 * Maps a retired topic from an earlier catalogue to the topics that replace it.
 * The retired topic has no row in `topics`.
 */
export const DBTopicMigrationSchema = z.object({
  id: UUIDSchema,
  subjectId: UUIDSchema,
  legacyTopicId: z.string().min(1),
  kindId: UUIDSchema,
  reason: AllCharLabelSchema.nullable(),
  createdAt: DBTimestampSchema,
});

export const DBTopicMigrationTargetSchema = z.object({
  migrationId: UUIDSchema,
  topicId: UUIDSchema,
  position: z.number().int(),
  createdAt: DBTimestampSchema,
});

/** A root question as a topic's question page lists it. */
export const DBTopicQuestionSummarySchema = z.object({
  id: UUIDSchema,
  bodyLatex: DBQuestionSchema.shape.bodyLatex,
  status: DBQuestionStatusSchema,
  types: z.array(z.object({ id: UUIDSchema, code: z.string(), label: z.string() })),
  totalMarks: z.number().int().min(0).nullable(),
  sourceLabel: z.string().nullable(),
  sourceType: z.string().nullable(),
  year: DBSourceDocumentYearSchema,
  originalQuestionId: DBQuestionOccurrenceSchema.shape.originalQuestionId.nullable(),
  originalQuestionNumber: DBQuestionOccurrenceSchema.shape.originalQuestionNumber.nullable(),
  partCount: z.number().int().min(0),
});

/** How many root questions sit under one topic, group, grouping or subject. */
export const DBTaxonomyQuestionCountSchema = z.object({
  kind: z.enum(TaxonomyNodeKind),
  id: UUIDSchema,
  questionCount: z.number().int().min(0),
});

/**
 * Client Schemas
 */

export const TopicKeySchema = DBTaxonomyKeySchema.openapi({
  description: 'Readable key for the topic, derived from its name',
  example: 'resolving-launch-velocity',
});

export const TopicNameSchema = DBTopicNameSchema.openapi({
  description: 'What the topic is called',
  example: 'Resolving launch velocity',
});

export const TopicStatusSchema = z.enum(TaxonomyStatus).openapi({
  description: 'Whether the topic is in use or retired',
  example: TaxonomyStatus.Active,
});

export const TopicSchema = z.object({
  id: UUIDSchema,
  key: TopicKeySchema,
  name: TopicNameSchema,
  description: DBTopicDescriptionSchema,
  kindId: UUIDSchema.nullable(),
  status: TopicStatusSchema,
  mergedIntoTopicId: UUIDSchema.nullable(),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const TopicAliasSchema = z.object({
  id: UUIDSchema,
  topicId: UUIDSchema,
  alias: AllCharLabelSchema.min(1),
  createdAt: UnixTimestampSchema,
});

export const TopicSubjectSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  sourceTopicId: z.string().min(1).nullable(),
  mappingScopeId: UUIDSchema,
  primaryGeneralGroupId: UUIDSchema.nullable(),
  taggingNote: AllCharLabelSchema.nullable(),
  reviewSources: z.array(z.string()).nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const TopicSubjectSyllabusSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  groupingId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});

export const TopicRelatedSpecificSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  specificTopicId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});

export const TopicMigrationSchema = z.object({
  id: UUIDSchema,
  subjectId: UUIDSchema,
  legacyTopicId: z.string().min(1),
  kindId: UUIDSchema,
  reason: AllCharLabelSchema.nullable(),
  createdAt: UnixTimestampSchema,
});

export const TopicMigrationTargetSchema = z.object({
  migrationId: UUIDSchema,
  topicId: UUIDSchema,
  position: z.number().int(),
  createdAt: UnixTimestampSchema,
});

export const TopicQuestionSummarySchema = z.object({
  id: UUIDSchema,
  bodyLatex: QuestionBodyLatexSchema,
  status: QuestionStatusSchema,
  types: z.array(z.object({ id: UUIDSchema, code: z.string(), label: z.string() })),
  totalMarks: z.number().int().min(0).nullable(),
  sourceLabel: z.string().nullable(),
  sourceType: z.string().nullable(),
  year: z.number().int().nullable(),
  originalQuestionId: QuestionOccurrenceOriginalQuestionIdSchema.nullable().openapi({
    description: 'What the listed source prints beside the question',
    example: '11(c)',
  }),
  originalQuestionNumber: QuestionOccurrenceOriginalQuestionNumberSchema.nullable().openapi({
    description: 'The number the listed source prints the question under',
    example: '11',
  }),
  partCount: z.number().int().min(0),
});

export const TaxonomyQuestionCountSchema = z.object({
  kind: z.enum(TaxonomyNodeKind),
  id: UUIDSchema,
  questionCount: z.number().int().min(0),
});
